import { FileDownload } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- client-side report, no server-side pagination
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { fetchFixtureReport } from '@sophocles/api/reports';
import DataGridToolbar, { ToolbarIconButton } from '@sophocles/data-grids/components/toolbar';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';

import { exportReportToExcel } from '../utils/reportExport';

const INITIAL_STATE = {
  sorting: { sortModel: [{ field: 'last_fixture_change_time', sort: 'desc' }] },
};

function formatChangeTime(changeTime) {
  return changeTime ? moment(changeTime).format('L, LTS') : t('general.unknown');
}

function FixtureReportGrid({ baseStructureLink, isBillboard, siteGroupId, siteGroup }) {
  const { t } = useTranslation();
  const apiRef = useGridApiRef();
  const relayLoadTerm = useRelayLoadTerm();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!siteGroupId) return;
    setLoading(true);
    fetchFixtureReport(siteGroupId)
      .then(data => {
        setRows(
          (data || []).map((row, idx) => ({
            id: row.id ?? idx,
            last_fixture_change_time: row.last_fixture_change_time ?? null,
            site_id: row.site_id ?? 0,
            site_name: row.site_name ?? '',
            site_description: row.site_description ?? '',
            site_address: row.site_address ?? '',
            name: row.name ?? '',
            relay_number: row.relay_number ?? '',
            number_of_fixtures: row.number_of_fixtures ?? 0,
            fixture: row.fixture ?? '',
          })),
        );
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [siteGroupId]);

  const columns = useMemo(
    () => [
      {
        field: 'last_fixture_change_time',
        headerName: t('reports.fixtureChangeTime', { defaultValue: 'Fixture Change Time' }),
        width: 200,
        // Sort on the underlying timestamp; render the locale-formatted string.
        valueGetter: value => (value ? new Date(value) : null),
        valueFormatter: value => (value ? moment(value).format('L, LTS') : t('general.unknown')),
      },
      {
        field: 'site_name',
        headerName: isBillboard
          ? t('reports.structure', { defaultValue: 'Structure' })
          : t('reports.site', { defaultValue: 'Site' }),
        flex: 1,
        minWidth: 160,
        renderCell: params => <Link to={`${baseStructureLink}${params.row.site_id}`}>{params.value}</Link>,
      },
      {
        field: 'site_description',
        headerName: isBillboard
          ? t('reports.structureDescription', { defaultValue: 'Structure Description' })
          : t('reports.siteDescription', { defaultValue: 'Site Description' }),
        flex: 1,
        minWidth: 200,
      },
      {
        field: 'site_address',
        headerName: t('reports.location', { defaultValue: 'Location' }),
        flex: 1,
        minWidth: 180,
      },
      { field: 'name', headerName: relayLoadTerm.singular, width: 120 },
      { field: 'relay_number', headerName: t('reports.relayNumber', { defaultValue: 'Relay #' }), width: 110 },
      { field: 'number_of_fixtures', headerName: t('reports.fixtureQty', { defaultValue: 'Fixture Qty' }), width: 120 },
      {
        field: 'fixture',
        headerName: t('reports.fixtureType', { defaultValue: 'Fixture Type' }),
        flex: 1,
        minWidth: 160,
      },
    ],
    [t, isBillboard, baseStructureLink, relayLoadTerm],
  );

  const handleExport = useCallback(() => {
    // getExcelFile reads row[field] verbatim, so format the timestamp column
    // for export — and keep desc-by-change-time order in the Excel file too.
    const exportRows = [...rows]
      .sort((a, b) => {
        const av = a.last_fixture_change_time ? new Date(a.last_fixture_change_time).getTime() : 0;
        const bv = b.last_fixture_change_time ? new Date(b.last_fixture_change_time).getTime() : 0;
        return bv - av;
      })
      .map(row => ({ ...row, last_fixture_change_time: formatChangeTime(row.last_fixture_change_time) }));
    exportReportToExcel({
      title: t('reports.fixtureReport'),
      fileName: 'fixture-report',
      rows: exportRows,
      columns,
      siteGroup,
      isBillboard,
    });
  }, [rows, columns, siteGroup, isBillboard]);

  const exportButton = useMemo(
    () => (
      <ToolbarIconButton
        tooltip={t('general.export', { defaultValue: 'Export' })}
        onClick={handleExport}
        disabled={loading || rows.length === 0}
      >
        {loading ? <CircularProgress size={18} /> : <FileDownload sx={{ fontSize: 18 }} />}
      </ToolbarIconButton>
    ),
    [t, handleExport, loading, rows.length],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <DataGridToolbar apiRef={apiRef} toolbarActions={exportButton} quickFilterPlaceholder={t('general.search')} />
      <DataGridPro
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        loading={loading}
        density="compact"
        hideFooter
        disableRowSelectionOnClick
        initialState={INITIAL_STATE}
        localeText={{ noRowsLabel: t('general.noResultsMessage') }}
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Box>
  );
}

export default FixtureReportGrid;
